import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import Typography from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Typography';

import AdvancedTypography from 'Components/UI/AdvancedTypography';
import { LEGAL_DOCUMENT_MAP } from 'Consts/LegalDocuments';
import RichTextDisplay from 'Components/UI/RichTextDisplay';
import { useClientAgreementContext } from '../ClientAgreementContext';
import InfoBox from '../../../Components/InfoBox';

const styles = (theme) => ({
  root: {
    paddingBottom: theme.spacing(3),
  },
  bullet: {
    ...theme.typography.h6,
    color: theme.palette.facet.primaryGray,
  },
  noListMargin: {
    '& ul': {
      marginTop: '0px',
      marginBottom: '0px',
    },
  },
});

const hasContent = (paragraph) => paragraph?.content;

const ClientAgreementTextCardDynamic = ({
  className,
  classes,
}) => {
  const { billingInformationCollection } = useClientAgreementContext();
  const [billingInformationResource] = billingInformationCollection;

  const topSectionParagraphs = [
    {
      key: 'agentAuthorizationText',
      content: billingInformationResource.attr('agentAuthorizationText'),
    },
    {
      key: 'agreementAcknowledgementBlurb',
      content: 'As part of this agreement, I acknowledge and agree:',
    },
  ].filter(hasContent);

  const topSectionListItems = [
    {
      key: 'membershipFee',
      content: billingInformationResource.attr('membershipFeeText'),
    },
    {
      key: 'payment',
      content: billingInformationResource.attr('paymentText'),
    },
    {
      key: 'renewal',
      content: billingInformationResource.attr('renewalInformationText'),
    },
    {
      key: 'informationIsCorrect',
      content: 'The information I have provided is correct.',
    },
    {
      key: 'moneyBackGuaranteeText',
      content: billingInformationResource.attr('moneyBackGuaranteeText'),
    },
  ].filter(hasContent);

  const bottomSectionListItems = [
    {
      key: 'taxWithHoldingAcknowledgement',
      content: billingInformationResource.attr('backupTaxWithHoldingAcknowledgementText'),
    },
    {
      key: 'authorizedPersonsListedAcknowledgement',
      content: billingInformationResource.attr('authorizedPersonsListedAcknowledgementText'),
    },
    {
      key: 'estatePlanningText',
      content: billingInformationResource.attr('estatePlanningText'),
    },
  ].filter(hasContent);

  const additionalPlanningAgreementLanguage = billingInformationResource
    .attr('serviceAgreementLineItems')
    .resources()
    .list()
    .map(lineItem => lineItem.attr('planningAgreementLanguage'))
    .filter(language => !!language) // Ignore empty language
    .join('');

  return (
    <InfoBox className={classNames(classes.root, classes.noListMargin, className)}>
      {topSectionParagraphs.map(section => {
        return (
          <Typography
            key={section.key}
            variant={'h6'}
            color={'primaryGray'}
            paragraph
          >
            {section.content}
          </Typography>
        );
      })}
      <ul>
        {topSectionListItems.map(section => {
          return (
            <Typography
              key={section.key}
              variant={'h6'}
              color={'primaryGray'}
              component={'li'}
            >
              {section.content}
            </Typography>
          );
        })}
        <AdvancedTypography
          className={classes.bullet}
          component={'li'}
          fragments={[
            {
              innerComponent: 'span',
              color: 'primaryGray',
              text: 'I will receive all communications electronically, including the ',
            },
            {
              innerComponent: 'a',
              color: 'accentBlue2',
              bold: true,
              text: LEGAL_DOCUMENT_MAP.PlanningAgreement.label,
              innerComponentProps: {
                target: '_blank',
                href: LEGAL_DOCUMENT_MAP.PlanningAgreement.href,
              },
            },
            {
              innerComponent: 'span',
              color: 'primaryGray',
              text: ', ',
            },
            {
              innerComponent: 'a',
              color: 'accentBlue2',
              bold: true,
              text: LEGAL_DOCUMENT_MAP.FormADVPart2A.label,
              innerComponentProps: {
                target: '_blank',
                href: LEGAL_DOCUMENT_MAP.FormADVPart2A.href,
              },
            },
            {
              innerComponent: 'span',
              color: 'primaryGray',
              text: ', ',
            },
            {
              innerComponent: 'a',
              color: 'accentBlue2',
              bold: true,
              text: '2B',
              innerComponentProps: {
                target: '_blank',
                href: LEGAL_DOCUMENT_MAP.FormABVPart2B.href,
              },
            },
            {
              innerComponent: 'span',
              color: 'primaryGray',
              text: ', and ',
            },
            {
              innerComponent: 'a',
              color: 'accentBlue2',
              bold: true,
              text: 'Form CRS',
              innerComponentProps: {
                target: '_blank',
                href: LEGAL_DOCUMENT_MAP.FormADVPart3.href,
              },
            },
            {
              innerComponent: 'span',
              color: 'primaryGray',
              text: ' (click the Legal Documents links to view these).',
            },
          ]}
        />
        {bottomSectionListItems
          .filter(listItem => listItem.content)
          .map(listItem => (
            <Typography
              key={listItem.key}
              variant={'h6'}
              color={'primaryGray'}
              component={'li'}
            >
              {listItem.content}
            </Typography>
          ))
        }
      </ul>
      {additionalPlanningAgreementLanguage && (
      <Typography
        variant="h6"
        color="primaryGray"
        component="div"
      >
        <RichTextDisplay content={additionalPlanningAgreementLanguage} resetFontSize={false} />
      </Typography>
      )}
      {billingInformationResource.attr('initialFeeRecap') && (
      <Typography
        variant="h6"
        color="primaryGray"
        component="p"
      >
        {billingInformationResource.attr('initialFeeRecap')}
      </Typography>
      )}
    </InfoBox>
  );
};

ClientAgreementTextCardDynamic.defaultProps = {
  className: '',
};

ClientAgreementTextCardDynamic.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(ClientAgreementTextCardDynamic);
